import { UseQueryResult, useQuery } from "@tanstack/react-query";

import type { PaymentsOrders } from "~/api/Api";
import { PaymentsOrdersResponse } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { Authorizations } from "~/api/types";
import { useTeam } from "~/contexts/TeamProvider";
import { orderQueries } from "~/pages/services/queryKeys";

export const COLLECTION_ACTIONS = ["create"];

type PendingOrdersResult = UseQueryResult<OrdersWithPermissions> & {
  orders: PaymentsOrders;
  createPermission: boolean;
};

type PaymentsOrdersResponseWithAuth = PaymentsOrdersResponse & {
  authorizations?: Authorizations;
};

type OrdersWithPermissions = {
  orders: PaymentsOrders;
  collectionPermissions: string[];
};

function extractAuthCollection(data: Authorizations | undefined): string[] {
  if (Array.isArray(data?.collection)) return data.collection;

  return [];
}

export function usePendingOrders(): PendingOrdersResult {
  const { team } = useTeam();

  if (!team) {
    throw new Error("Current team is not available.");
  }

  const query = useQuery<OrdersWithPermissions>({
    retry: false,
    refetchOnWindowFocus: false,
    queryKey: orderQueries.pending({
      teamId: team.id,
      actions: COLLECTION_ACTIONS,
    }),
    queryFn: async () => {
      const response = await apiClient.billing.ordersList(
        {
          type: "active",
          q: {
            status_eq: "pending",
          },
        },
        {
          headers: {
            "Authorize-Collection-Actions": COLLECTION_ACTIONS.join(","),
          },
        },
      );

      const data = response.data as PaymentsOrdersResponseWithAuth;

      const orders = (data?.["payments/orders"] ?? []) as PaymentsOrders;
      const collectionPermissions = extractAuthCollection(data.authorizations);

      return { orders, collectionPermissions };
    },
  });

  const orders = (query.data?.orders ?? []) as PaymentsOrders;
  const createPermission =
    query.data?.collectionPermissions.includes("create") ?? false;

  return {
    ...query,
    orders,
    createPermission,
  };
}
